import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { api } from "../../shared/api/client";
import type {
  ClassState,
  GoalCheckinResult,
  GoalFollowUps,
  GoalStatus,
  GrowthEvidence,
  StudentGoal
} from "../../shared/api/types";
import { image } from "../../shared/assets/manifest";
import { can } from "../../shared/auth/capabilities";
import { Button } from "../../shared/ui/Button";
import { EmptyState } from "../../shared/ui/EmptyState";
import { Field } from "../../shared/ui/Field";
import { LoadingBlock } from "../../shared/ui/LoadingBlock";
import { ConfirmDialog, Modal, ModalFooter } from "../../shared/ui/Modal";
import { errorMessage, useToast } from "../../shared/ui/ToastProvider";
import styles from "./GoalsPage.module.css";

const DIMENSIONS = [
  ["academic", "学业"], ["behavior", "行为"], ["social", "社交"],
  ["character", "品格"], ["milestone", "里程碑"], ["other", "其他"]
] as const;
const STATUS: Record<GoalStatus, string> = { active: "进行中", paused: "已暂停", achieved: "已达成", closed: "已关闭" };
const RESULTS: Record<GoalCheckinResult, string> = { on_track: "进展正常", at_risk: "需要关注", achieved: "已经达成" };

type GoalPage = { items: StudentGoal[]; total: number; page: number; pageSize: number };
type GoalDraft = {
  studentId: string; title: string; dimension: string; baseline: string;
  successCriteria: string; startDate: string; dueDate: string;
};

function plusDays(days: number) {
  const value = new Date();
  value.setDate(value.getDate() + days);
  return value.toISOString().slice(0, 10);
}

const emptyDraft = (): GoalDraft => ({
  studentId: "", title: "", dimension: "behavior", baseline: "", successCriteria: "",
  startDate: plusDays(0), dueDate: plusDays(28)
});

function dimensionLabel(value: string) {
  return DIMENSIONS.find(([id]) => id === value)?.[1] || value;
}

export function FollowUpPanel({ state, compact = false }: { state: ClassState; compact?: boolean }) {
  const query = useQuery({
    queryKey: ["goal-follow-ups", state.classId],
    queryFn: async () => (await api<GoalFollowUps>("/api/follow-ups")).data!
  });
  if (!state.features?.goals || query.isLoading || !query.data) return null;
  const data = query.data;
  const total = data.counts.overdue + data.counts.atRisk + data.counts.interventions;
  return (
    <section className={`${styles.followUps} ${compact ? styles.compact : ""}`} aria-label="本周待跟进">
      <div>
        <strong>本周待跟进</strong>
        <span>{total ? "优先处理风险和逾期事项" : "目前没有待跟进事项"}</span>
      </div>
      <dl>
        <div><dt>逾期目标</dt><dd>{data.counts.overdue}</dd></div>
        <div><dt>风险检查</dt><dd>{data.counts.atRisk}</dd></div>
        <div><dt>到期干预</dt><dd>{data.counts.interventions}</dd></div>
      </dl>
    </section>
  );
}

function GoalForm({ state, goal, onClose }: { state: ClassState; goal?: StudentGoal; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<GoalDraft>(() => goal ? {
    studentId: String(goal.studentId), title: goal.title, dimension: goal.dimension,
    baseline: goal.baseline, successCriteria: goal.successCriteria,
    startDate: goal.startDate, dueDate: goal.dueDate
  } : emptyDraft());
  const save = useMutation({
    mutationFn: () => api(goal ? `/api/goals/${goal.id}` : "/api/goals", {
      method: goal ? "PATCH" : "POST",
      body: JSON.stringify({ ...draft, studentId: Number(draft.studentId) })
    }),
    onSuccess: async () => {
      toast(goal ? "目标已更新" : "目标已创建", "success");
      await queryClient.invalidateQueries({ queryKey: ["goals"] });
      await queryClient.invalidateQueries({ queryKey: ["goal-follow-ups"] });
      onClose();
    },
    onError: (error) => toast(errorMessage(error, "保存目标失败"), "error")
  });
  const patch = (key: keyof GoalDraft, value: string) => setDraft((current) => ({ ...current, [key]: value }));
  const valid = draft.studentId && draft.title.trim() && draft.successCriteria.trim() && draft.startDate && draft.dueDate;
  return (
    <Modal title={goal ? "编辑成长目标" : "新建成长目标"} variant="form" onClose={onClose}
      footer={<ModalFooter onCancel={onClose} onSubmit={() => save.mutate()} loading={save.isPending} submitDisabled={!valid} />}>
      <div className={styles.formGrid}>
        <Field label="学生">
          <select value={draft.studentId} disabled={Boolean(goal)} onChange={(e) => patch("studentId", e.target.value)}>
            <option value="">请选择学生</option>
            {state.students.map((student) => <option key={student.id} value={student.id}>{student.name}</option>)}
          </select>
        </Field>
        <Field label="成长维度">
          <select value={draft.dimension} onChange={(e) => patch("dimension", e.target.value)}>
            {DIMENSIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </Field>
        <Field label="目标标题" className={styles.wide}>
          <input maxLength={100} value={draft.title} onChange={(e) => patch("title", e.target.value)} placeholder="例如：建立课前准备习惯" />
        </Field>
        <Field label="当前基线" className={styles.wide}>
          <textarea maxLength={1000} rows={3} value={draft.baseline} onChange={(e) => patch("baseline", e.target.value)} placeholder="用客观事实描述当前情况" />
        </Field>
        <Field label="成功标准" className={styles.wide}>
          <textarea maxLength={1000} rows={3} value={draft.successCriteria} onChange={(e) => patch("successCriteria", e.target.value)} placeholder="写成可观察、可复盘的标准" />
        </Field>
        <Field label="开始日期"><input type="date" value={draft.startDate} onChange={(e) => patch("startDate", e.target.value)} /></Field>
        <Field label="到期日期"><input type="date" value={draft.dueDate} onChange={(e) => patch("dueDate", e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

function CheckinDialog({ goal, onClose }: { goal: StudentGoal; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<GoalCheckinResult>("on_track");
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: () => api(`/api/goals/${goal.id}/checkins`, { method: "POST", body: JSON.stringify({ result, note }) }),
    onSuccess: async () => {
      toast(result === "achieved" ? "目标已标记达成" : "检查结果已记录", "success");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["goals"] }),
        queryClient.invalidateQueries({ queryKey: ["goal", goal.id] }),
        queryClient.invalidateQueries({ queryKey: ["goal-follow-ups"] })
      ]);
      onClose();
    },
    onError: (error) => toast(errorMessage(error, "记录检查失败"), "error")
  });
  return (
    <Modal title={`${goal.studentName} · 目标检查`} variant="form" onClose={onClose}
      footer={<ModalFooter onCancel={onClose} onSubmit={() => save.mutate()} loading={save.isPending} submitLabel="记录检查" />}>
      <p className={styles.dialogLead}>{goal.title}</p>
      <div className={styles.segmented}>
        {(Object.keys(RESULTS) as GoalCheckinResult[]).map((value) => (
          <button type="button" key={value} className={result === value ? styles.selected : ""} onClick={() => setResult(value)}>{RESULTS[value]}</button>
        ))}
      </div>
      <Field label="观察说明"><textarea rows={4} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="记录观察到的事实和下一步" /></Field>
    </Modal>
  );
}

function GoalDetailModal({ goalId, writable, onEdit, onCheckin, onClose }: {
  goalId: number; writable: boolean; onEdit: (goal: StudentGoal) => void;
  onCheckin: (goal: StudentGoal) => void; onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["goal", goalId], queryFn: async () => (await api<StudentGoal>(`/api/goals/${goalId}`)).data! });
  const [action, setAction] = useState("");
  const [plannedDate, setPlannedDate] = useState(plusDays(7));
  const [evidenceId, setEvidenceId] = useState("");
  const goal = query.data;
  const evidence = useQuery({
    queryKey: ["growth-for-goal", goal?.studentId], enabled: Boolean(goal?.studentId),
    queryFn: async () => (await api<{ items: GrowthEvidence[] }>(`/api/growth-evidence?studentId=${goal!.studentId}&pageSize=100`)).data?.items || []
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["goal", goalId] });
    await queryClient.invalidateQueries({ queryKey: ["goal-follow-ups"] });
  };
  const intervention = useMutation({
    mutationFn: () => api(`/api/goals/${goalId}/interventions`, { method: "POST", body: JSON.stringify({ action, plannedDate }) }),
    onSuccess: async () => { setAction(""); toast("干预措施已安排", "success"); await refresh(); },
    onError: (error) => toast(errorMessage(error, "安排干预失败"), "error")
  });
  const complete = useMutation({
    mutationFn: (id: number) => api(`/api/goal-interventions/${id}`, { method: "PATCH", body: JSON.stringify({ status: "completed" }) }),
    onSuccess: async () => { toast("干预已完成", "success"); await refresh(); }
  });
  const link = useMutation({
    mutationFn: () => api(`/api/goals/${goalId}/evidence/${evidenceId}`, { method: "POST" }),
    onSuccess: async () => { setEvidenceId(""); toast("成长证据已关联", "success"); await refresh(); }
  });
  if (query.isLoading || !goal) return <Modal title="目标详情" onClose={onClose}><LoadingBlock /></Modal>;
  return (
    <Modal title={`${goal.studentName} · ${goal.title}`} onClose={onClose} mascot={false}
      footer={<><Button variant="secondary" onClick={onClose}>关闭</Button>{writable ? <Button onClick={() => onEdit(goal)}>编辑目标</Button> : null}</>}>
      <div className={styles.detailHeader}>
        <span className={`${styles.status} ${styles[goal.status]}`}>{STATUS[goal.status]}</span>
        <span>{dimensionLabel(goal.dimension)}</span><span>{goal.startDate} 至 {goal.dueDate}</span>
      </div>
      <div className={styles.detailGrid}>
        <section><h4>当前基线</h4><p>{goal.baseline || "未填写"}</p></section>
        <section><h4>成功标准</h4><p>{goal.successCriteria}</p></section>
      </div>
      <section className={styles.timelineSection}>
        <div className={styles.sectionHeading}><h4>检查记录</h4>{writable && goal.status === "active" ? <Button size="small" onClick={() => onCheckin(goal)}>新增检查</Button> : null}</div>
        {goal.checkins?.length ? goal.checkins.map((item) => <article className={styles.timelineItem} key={item.id}><b>{RESULTS[item.result]}</b><span>{item.happenedAt.slice(0, 10)}</span><p>{item.note || "未填写说明"}</p></article>) : <p className={styles.muted}>还没有检查记录</p>}
      </section>
      <section className={styles.timelineSection}>
        <h4>干预措施</h4>
        {goal.interventions?.map((item) => <article className={styles.intervention} key={item.id}><div><b>{item.action}</b><span>{item.plannedDate || "未设日期"} · {item.status === "completed" ? "已完成" : item.status === "cancelled" ? "已取消" : "待执行"}</span></div>{writable && item.status === "planned" ? <Button size="small" variant="secondary" onClick={() => complete.mutate(item.id)}>完成</Button> : null}</article>)}
        {writable && goal.status === "active" ? <div className={styles.inlineForm}><input value={action} onChange={(e) => setAction(e.target.value)} placeholder="安排一项具体干预" /><input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} /><Button size="small" disabled={!action.trim()} onClick={() => intervention.mutate()}>添加</Button></div> : null}
      </section>
      <section className={styles.timelineSection}>
        <h4>关联证据</h4>
        {goal.evidence?.map((item) => <article className={styles.evidence} key={item.id}><b>{item.title}</b><span>{item.happenedAt.slice(0, 10)}</span></article>)}
        {writable ? <div className={styles.inlineForm}><select value={evidenceId} onChange={(e) => setEvidenceId(e.target.value)}><option value="">选择该生的成长记录</option>{evidence.data?.filter((item) => !goal.evidence?.some((linked) => linked.id === item.id)).map((item) => <option value={item.id} key={item.id}>{item.title}</option>)}</select><Button size="small" disabled={!evidenceId} onClick={() => link.mutate()}>关联</Button></div> : null}
      </section>
      <section className={styles.aggregate}><div><span>目标期内积分</span><strong>{goal.aggregates?.points.total ?? 0}</strong><small>{goal.aggregates?.points.count ?? 0} 条记录</small></div><div><span>成绩记录</span><strong>{goal.aggregates?.scores.length ?? 0}</strong><small>目标期内科目成绩</small></div><div><span>考勤异常</span><strong>{(goal.aggregates?.attendance.counts.late ?? 0) + (goal.aggregates?.attendance.counts.leave ?? 0) + (goal.aggregates?.attendance.counts.absent ?? 0)}</strong><small>{goal.aggregates?.attendance.finalizedDays ?? 0} 个已完成考勤日</small></div></section>
    </Modal>
  );
}

export function GoalsPage({ state }: { state: ClassState }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const writable = can(state.currentTeacher, "goals.write");
  const [studentId, setStudentId] = useState("");
  const [status, setStatus] = useState("active");
  const [keyword, setKeyword] = useState("");
  const [form, setForm] = useState<StudentGoal | "new" | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [checkin, setCheckin] = useState<StudentGoal | null>(null);
  const [removing, setRemoving] = useState<StudentGoal | null>(null);
  const [reviewMode, setReviewMode] = useState(false);
  const params = useMemo(() => {
    const query = new URLSearchParams({ pageSize: "100" });
    if (studentId) query.set("studentId", studentId);
    if (status) query.set("status", status);
    if (keyword.trim()) query.set("keyword", keyword.trim());
    return query.toString();
  }, [keyword, status, studentId]);
  const goals = useQuery({
    queryKey: ["goals", state.classId, params],
    queryFn: async () => (await api<GoalPage>(`/api/goals?${params}`)).data!
  });
  const remove = useMutation({
    mutationFn: (id: number) => api(`/api/goals/${id}`, { method: "DELETE" }),
    onSuccess: async () => { toast("目标已删除", "warning"); await queryClient.invalidateQueries({ queryKey: ["goals"] }); }
  });
  if (state.features && !state.features.goals) return <EmptyState title="成长目标尚未开启" detail="该模块完成生产验收后会在此显示。" />;
  const items = goals.data?.items || [];
  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div><span>持续跟进，不只记录结果</span><h2>学生成长目标</h2><p>把目标、检查、干预和成长证据连成一条清晰时间线。</p></div>
        <img src={image.growthFlower} alt="" />
        {writable ? <Button onClick={() => setForm("new")}>新建目标</Button> : null}
      </header>
      <FollowUpPanel state={state} />
      <section className={styles.toolbar}>
        <input aria-label="搜索目标" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="搜索学生、目标或成功标准" />
        <select aria-label="筛选学生" value={studentId} onChange={(e) => setStudentId(e.target.value)}><option value="">全部学生</option>{state.students.map((student) => <option key={student.id} value={student.id}>{student.name}</option>)}</select>
        <select aria-label="筛选状态" value={status} onChange={(e) => setStatus(e.target.value)}><option value="all">全部状态</option>{(Object.keys(STATUS) as GoalStatus[]).map((key) => <option key={key} value={key}>{STATUS[key]}</option>)}</select>
        {writable ? <Button variant="secondary" onClick={() => setReviewMode((value) => !value)}>{reviewMode ? "退出复盘" : "批量复盘"}</Button> : null}
      </section>
      {goals.isLoading ? <LoadingBlock /> : null}
      {!goals.isLoading && !items.length ? <EmptyState title="还没有成长目标" detail="为需要持续支持的学生建立第一个具体目标。" /> : null}
      <div className={reviewMode ? styles.reviewGrid : styles.goalGrid}>
        {items.map((goal) => <article className={styles.goalCard} key={goal.id}>
          <div className={styles.cardTop}><div><span>{goal.studentName}</span><h3>{goal.title}</h3></div><span className={`${styles.status} ${styles[goal.status]}`}>{STATUS[goal.status]}</span></div>
          <p className={styles.criteria}>{goal.successCriteria}</p>
          <div className={styles.meta}><span>{dimensionLabel(goal.dimension)}</span><span>到期 {goal.dueDate}</span>{goal.latestCheckinResult ? <span className={goal.latestCheckinResult === "at_risk" ? styles.risk : ""}>{RESULTS[goal.latestCheckinResult]}</span> : <span>尚未检查</span>}</div>
          <div className={styles.cardActions}><Button size="small" variant="secondary" onClick={() => setDetailId(goal.id)}>详情</Button>{writable && goal.status === "active" ? <Button size="small" onClick={() => setCheckin(goal)}>快速检查</Button> : null}{writable && !reviewMode ? <button className={styles.textButton} type="button" onClick={() => setForm(goal)}>编辑</button> : null}{writable && !reviewMode ? <button className={styles.textButton} type="button" onClick={() => setRemoving(goal)}>删除</button> : null}</div>
        </article>)}
      </div>
      {form ? <GoalForm state={state} goal={form === "new" ? undefined : form} onClose={() => setForm(null)} /> : null}
      {checkin ? <CheckinDialog goal={checkin} onClose={() => setCheckin(null)} /> : null}
      {detailId ? <GoalDetailModal goalId={detailId} writable={writable} onClose={() => setDetailId(null)} onEdit={(goal) => { setDetailId(null); setForm(goal); }} onCheckin={(goal) => setCheckin(goal)} /> : null}
      {removing ? <ConfirmDialog danger title="删除成长目标" detail={`确定删除“${removing.title}”吗？相关检查和干预将不再展示。`} onClose={() => setRemoving(null)} onConfirm={async () => { await remove.mutateAsync(removing.id); setRemoving(null); }} /> : null}
    </div>
  );
}
